import {IconButton} from '@wandb/components';
import {useEffectExceptFirstRender} from '@wandb/weave/common/util/hooks';
import {isEqual} from 'lodash';
import React, {useCallback, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Checkbox, Popup} from 'semantic-ui-react';

import {usePrevious} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import * as FilterActions from '../../state/views/filter/actions';
import {Ref} from '../../state/views/filter/types';
import * as ViewHooks from '../../state/views/hooks';
import {useViewAction} from '../../state/views/hooks';
import {DateFormat, format} from '../../util/date';
import * as Filter from '../../util/filters';
import * as FilterTypes from '../../util/filterTypes';
import {Config as TableSettings} from '../../util/runfeed';
import * as RunTypes from '../../util/runTypes';
import {FilterValueSelectorDate} from '../Filters/FilterValueSelector';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {RunsFilterTableAction} from './RunsFilterTableAction';

type UseFreezeFilterArgs = {filtersRef: Ref};

function useFreezeFilter({filtersRef}: UseFreezeFilterArgs) {
  const rootFilter = ViewHooks.usePart(filtersRef);
  const freezeFilterIndex = React.useMemo(() => {
    return Filter.findFreezeFilterIndex(rootFilter);
  }, [rootFilter]);

  if (freezeFilterIndex < 0) {
    return {freezeFilter: null, index: -1};
  }

  const freezeFilter = rootFilter.filters[0].filters[
    freezeFilterIndex
  ] as FilterTypes.IndividualFilter<RunTypes.Key>;

  return {
    freezeFilter,
    index: freezeFilterIndex,
  };
}

interface RunsFreezeTableActionProps {
  notificationOpen: boolean;
  pickerOpen: boolean;
  filtersRef: Ref;
  setPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFreezeChanged(): void;
}

export const RunsFreezeTableAction = (props: RunsFreezeTableActionProps) => {
  const {filtersRef, pickerOpen, setPickerOpen} = props;
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const {freezeFilter} = useFreezeFilter({filtersRef});
  const isFrozen = freezeFilter ? !freezeFilter.disabled : false;

  let buttonLabel = 'Freeze run set';
  if (freezeFilter && isFrozen) {
    const date = format(
      new Date(freezeFilter.value as string),
      DateFormat.MONTH_DAY_YEAR
    );
    // NBSP to prevent the date from being orphaned on its own line:
    // eslint-disable-next-line no-irregular-whitespace
    buttonLabel = `Run set frozen to include runs before ${date}`;
  }

  return (
    <Popup
      basic
      className="wb-table-action-popup"
      on="click"
      position="bottom left"
      open={pickerOpen}
      onOpen={() => setPickerOpen(true)}
      onClose={() => setPickerOpen(false)}
      trigger={
        <IconButton
          isActive={isFrozen}
          variant="secondary"
          icon="freeze-snowflake"
          aria-label={buttonLabel}
          tooltip={{children: buttonLabel}}
          onPress={() => {
            window.analytics?.track('Clicked freeze run set', {
              location: analyticsLocation,
            });
          }}
        />
      }
      content={
        <RunsFreezePicker
          filtersRef={props.filtersRef}
          onFreezeChanged={props.onFreezeChanged}
        />
      }
      popperModifiers={{
        preventOverflow: {enabled: false},
        flip: {enabled: false},
      }}
    />
  );
};

interface RunsFreezePickerProps {
  filtersRef: Ref;
  onFreezeChanged(): void;
}

export const RunsFreezePicker = (props: RunsFreezePickerProps) => {
  const {filtersRef, onFreezeChanged} = props;
  const {freezeFilter, index: freezeFilterIndex} = useFreezeFilter({
    filtersRef,
  });

  const rootFilter = ViewHooks.usePart(filtersRef);
  const setFiltersAction = useViewAction(filtersRef, FilterActions.set);

  const setFilters = useCallback(
    (...args: Parameters<typeof setFiltersAction>) => {
      setFiltersAction(...args);
      onFreezeChanged();
    },
    [setFiltersAction, onFreezeChanged]
  );

  React.useEffect(() => {
    // If a 'createdAt <=' filter doesn't exist, create it
    if (!freezeFilter) {
      setFilters(
        Filter.Update.groupPush(rootFilter, [0], {
          key: Filter.CREATED_AT_KEY,
          op: '<=',
          value: new Date().toISOString(),
          disabled: true,
        })
      );
    }
  }, [freezeFilter, rootFilter, setFilters]);

  const setFreezeFilterValue = React.useCallback(
    (partialFilter: Partial<FilterTypes.IndividualFilter<RunTypes.Key>>) => {
      setFilters(
        Filter.Update.setFilter(rootFilter, [0, freezeFilterIndex], {
          ...freezeFilter,
          ...partialFilter,
        } as FilterTypes.IndividualFilter<RunTypes.Key>)
      );
    },
    [freezeFilter, freezeFilterIndex, rootFilter, setFilters]
  );

  const toggleFreezeFilterDisabled = React.useCallback(() => {
    setFilters(
      Filter.Update.setFilter(rootFilter, [0, freezeFilterIndex], {
        ...freezeFilter,
        disabled: freezeFilter ? !freezeFilter.disabled : false,
      } as FilterTypes.IndividualFilter<RunTypes.Key>)
    );
  }, [freezeFilter, freezeFilterIndex, rootFilter, setFilters]);

  if (freezeFilter == null) {
    return <div />;
  }

  return (
    <div>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <div style={{marginTop: 6, marginRight: 8}}>
          <Checkbox
            checked={!freezeFilter.disabled}
            onChange={toggleFreezeFilterDisabled}
          />
        </div>
        <div>Only include runs created before</div>
      </div>
      <div style={{marginLeft: 18, marginTop: 4}}>
        <FilterValueSelectorDate
          value={freezeFilter.value as string | null}
          setFilter={setFreezeFilterValue}
        />
      </div>
    </div>
  );
};

interface RunsFreezeAndFilterProps {
  entityName: string;
  projectName: string;
  tableSettings: TableSettings;
  enableFreezeRunset: boolean;
  filtersRef: Ref;
  compact: boolean;
  onChange(): void;
}

// Freezing runs is the same as filtering by 'createdAt <=',
// so the Freeze and Filter button components share notification state in this wrapper component
export const RunsFreezeAndFilter = (props: RunsFreezeAndFilterProps) => {
  const {
    entityName,
    projectName,
    tableSettings,
    enableFreezeRunset,
    filtersRef,
    compact,
    onChange,
  } = props;

  const viewer = useViewer();
  const [freezePickerOpen, setFreezePickerOpen] = useState(false);
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);

  // Show popup notification when filters are changed outside of the filter popup
  // (e.g. parcoor selections-to-filters, or adding a filter via a table cell)
  // If the new filter matches "createdAt <=", we show a 'Run set frozen' notification on the freeze button
  // Otherwise, we show a 'Filters changed' notification on the filter button
  const [notificationPopupOpen, setNotificationPopupOpen] = useState<
    'freeze' | 'filter' | false
  >(false);
  const rootFilters = ViewHooks.usePart(filtersRef);
  const prevRootFilters = usePrevious(rootFilters);
  useEffectExceptFirstRender(() => {
    if (freezePickerOpen || filterPickerOpen) {
      return;
    }
    const isFrozen = Filter.findFreezeFilterIndex(rootFilters) >= 0;
    const prevFrozen =
      prevRootFilters && Filter.findFreezeFilterIndex(prevRootFilters) >= 0;
    if (!isEqual(prevRootFilters, rootFilters)) {
      setNotificationPopupOpen(isFrozen && !prevFrozen ? 'freeze' : 'filter');
      setTimeout(() => {
        setNotificationPopupOpen(false);
      }, 8000);
      return;
    }
  }, [freezePickerOpen, filterPickerOpen, rootFilters, prevRootFilters]);

  const defaultToggleFilters = React.useMemo(
    () => [
      {
        filter: Filter.usernameFilter(viewer?.username ?? ''),
        label: Filter.FILTER_LABEL_SHOW_MY_WORK,
      },
      {
        filter: Filter.hideCrashed,
        label: Filter.FILTER_LABEL_HIDE_CRASHED,
      },
    ],
    [viewer]
  );

  return (
    <>
      {enableFreezeRunset && (
        <RunsFreezeTableAction
          filtersRef={filtersRef}
          onFreezeChanged={onChange}
          pickerOpen={freezePickerOpen}
          setPickerOpen={setFreezePickerOpen}
          notificationOpen={notificationPopupOpen === 'freeze'}
        />
      )}
      <RunsFilterTableAction
        entityName={entityName}
        projectName={projectName}
        tableSettings={tableSettings}
        filtersRef={filtersRef}
        compact={compact}
        pickerOpen={filterPickerOpen}
        notificationOpen={notificationPopupOpen === 'filter'}
        setPickerOpen={setFilterPickerOpen}
        defaultToggleFilters={defaultToggleFilters}
        onFiltersChanged={onChange}
      />
    </>
  );
};
